import {
  Modal,
  Box,
  MenuItem,
  Typography,
  Button,
  IconButton,
  Select,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";

import { Info } from "lucide-react";

import VersionSelector from "./version-selector";

const FilterModal = ({
  open,
  handleClose,
  filters,
  setFilters,
  handleApplyFilters,
  handleResetFilters,
}: any) => {
  const handleChange = (field: any) => (event: any) => {
    if (field === "documentVersion") {
      const value = event;
      setFilters((prevFilters: any) => ({
        ...prevFilters,
        [field]: typeof value === "string" ? value.split(",") : value,
      }));
    } else {
      const { value } = event.target;
      setFilters((prevFilters: any) => ({
        ...prevFilters,
        [field]: typeof value === "string" ? value.split(",") : value,
      }));
    }
  };

  const totalFiltersApplied = Object.values(filters).filter(
    (value) => value.length > 0
  ).length;

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "background.paper",
          borderRadius: "8px",
          boxShadow: 24,
          p: 4,
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "grey.500",
          }}
        >
          <CloseIcon />
        </IconButton>

        <div
          className="bg-brand-primary py-4 px-6 w-full flex justify-center"
          style={{ alignItems: "center" }}
        >
          <h2 className="text-xl font-semibold text-center text-white">
            Filter Options
          </h2>
          <HoverCard>
            <HoverCardTrigger asChild>
              <Info className="ml-2 text-white" />
            </HoverCardTrigger>
            <HoverCardContent className="w-80">
              <div className="flex justify-between space-x-4">
                <div className="space-y-1">
                  <h4 className="text-sm font-semibold">Info</h4>
                  <p className="text-sm">
                    Total of 30 filter disjunctions can be applied
                  </p>
                </div>
              </div>
            </HoverCardContent>
          </HoverCard>
        </div>

        <Typography
          variant="subtitle1"
          component="p"
          mb={2}
          sx={{ color: "gray", textAlign: "center" }}
        >
          Total Filters Applied: {totalFiltersApplied}
        </Typography>

        {/* CPLM Status */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Select CPLM Status</InputLabel>
          <Select
            multiple
            value={filters.cplmStatus || []}
            onChange={handleChange("cplmStatus")}
            renderValue={(selected) => selected.join(", ")}
          >
            {["Pending Approval", "Approved", "Open", "Unknown"].map(
              (status) => (
                <MenuItem key={status} value={status}>
                  <Checkbox
                    checked={filters.cplmStatus?.includes(status) || false}
                  />
                  <ListItemText primary={status} />
                </MenuItem>
              )
            )}
          </Select>
        </FormControl>

        {/* Product Selection */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Select a Product</InputLabel>
          <Select
            multiple
            value={filters.product || []}
            onChange={handleChange("product")}
            renderValue={(selected) => selected.join(", ")}
          >
            <MenuItem key="Dexcom G7" value="Dexcom G7">
              <Checkbox
                checked={filters.product?.includes("Dexcom G7") || false}
              />
              <ListItemText primary="Dexcom G7" />
            </MenuItem>
            <MenuItem key="Dexcom Stelo" value="Dexcom Stelo">
              <Checkbox
                checked={filters.product?.includes("Dexcom Stelo") || false}
              />
              <ListItemText primary="Dexcom Stelo" />
            </MenuItem>
            <MenuItem key="Dexcom Proton" value="Dexcom Proton">
              <Checkbox
                checked={filters.product?.includes("Dexcom Proton") || false}
              />
              <ListItemText primary="Dexcom Proton" />
            </MenuItem>
          </Select>
        </FormControl>

        {/* Design Step Selection */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Select a Design Step</InputLabel>
          <Select
            multiple
            value={filters.designStep || []}
            onChange={handleChange("designStep")}
            renderValue={(selected) => selected.join(", ")}
          >
            {[
              "Technical Design Review Document",
              "Design Verification",
              "UI Design",
              "Design Change",
              "Design Input",
              "Standards",
              "Architecture",
              "User Guide",
              "Design Output",
              "Risk",
              "Risk Management",
              "Other",
            ].map((step) => (
              <MenuItem key={step} value={step}>
                <Checkbox
                  checked={filters.designStep?.includes(step) || false}
                />
                <ListItemText primary={step} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Document Type Selection */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Select a Document Type</InputLabel>
          <Select
            multiple
            value={filters.documentType || []}
            onChange={handleChange("documentType")}
            renderValue={(selected) => selected.join(", ")}
          >
            {[
              "Needs",
              "SDS",
              "PDFs_SRS",
              "RA",
              "System Product Characteristics",
              "SDAD",
              "LRS",
              "DOC",
            ].map((type) => (
              <MenuItem key={type} value={type}>
                <Checkbox
                  checked={filters.documentType?.includes(type) || false}
                />
                <ListItemText primary={type} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <VersionSelector
          handleChange={handleChange}
          selection={filters?.documentVersion}
        />

        {/* Document Version Selection */}
        {/* <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Select a Document Version</InputLabel>
          <Select
            multiple
            value={filters.documentVersion || []}
            onChange={handleChange("documentVersion")}
            renderValue={(selected) => selected.join(", ")}
          >
            {[0.0, 1.0, 2.0, 3.0, 4.0, 5.0, 15.0, 34.0].map((version) => (
              <MenuItem key={version} value={version}>
                <Checkbox
                  checked={filters.documentVersion?.includes(version) || false}
                />
                <ListItemText primary={version} />
              </MenuItem>
            ))}
          </Select>
        </FormControl> */}

        {/* Action Buttons */}
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}
        >
          {/* <Button
            variant="contained"
            color="success"
            onClick={handleApplyFilters}
          >
            Apply Filters
          </Button> */}
          <Button
            variant="contained"
            color="error"
            onClick={handleResetFilters}
          >
            Reset Filters
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default FilterModal;
